import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback, useMemo, } from "react";
import { Input } from "@material-tailwind/react";
function normalizeString(str) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}
function SelectWithSearch({ label, options, selectedItem, onSelect, getOptionLabel, inputRef, error, allowCreate = false, onCreate, getCreateOptionLabel, }) {
    const [search, setSearch] = useState("");
    const [show, setShow] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const containerRef = useRef(null);
    // Default getOptionLabel function.
    const defaultGetOptionLabel = useCallback((option) => typeof option === "string" ? option : option.label, []);
    const optionLabel = getOptionLabel || defaultGetOptionLabel;
    const trimmedSearch = search.trim();
    // Filter options based on the search string, ignoring diacritics.
    const filteredOptions = useMemo(() => {
        const normalizedSearch = normalizeString(search).toLowerCase();
        return options.filter((option) => normalizeString(optionLabel(option))
            .toLowerCase()
            .includes(normalizedSearch));
    }, [options, search, optionLabel]);
    const hasExactMatch = useMemo(() => {
        if (!trimmedSearch) {
            return false;
        }
        const normalizedSearch = normalizeString(trimmedSearch).toLowerCase();
        return options.some((option) => normalizeString(optionLabel(option)).toLowerCase() === normalizedSearch);
    }, [options, optionLabel, trimmedSearch]);
    // Close dropdown if click occurs outside.
    const handleClickOutside = useCallback((event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
            setShow(false);
        }
    }, []);
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [handleClickOutside]);
    // Open dropdown when input is focused.
    const handleFocus = useCallback(() => {
        setShow(true);
    }, []);
    // Handle selecting an option.
    const handleSelect = useCallback((option) => {
        onSelect(option);
        setSearch(""); // Reset search text after selection.
        setShow(false);
    }, [onSelect]);
    const handleCreate = useCallback(async () => {
        if (!allowCreate || !onCreate || !trimmedSearch || isCreating) {
            return;
        }
        setIsCreating(true);
        try {
            const createdOption = await onCreate(trimmedSearch);
            if (createdOption) {
                handleSelect(createdOption);
            }
        }
        finally {
            setIsCreating(false);
        }
    }, [allowCreate, handleSelect, isCreating, onCreate, trimmedSearch]);
    const handleKeyDown = useCallback((event) => {
        if (event.key === "Enter" && show) {
            event.preventDefault();
            if (filteredOptions.length > 0) {
                handleSelect(filteredOptions[0]);
            }
            else if (allowCreate && !hasExactMatch) {
                void handleCreate();
            }
        }
    }, [allowCreate, filteredOptions, handleCreate, handleSelect, hasExactMatch, show]);
    return (_jsxs("div", { className: "mt-4 relative", ref: containerRef, children: [_jsx(Input, { variant: "standard", type: "text", placeholder: `Rechercher ${label}`, label: label, value: show ? search : selectedItem ? optionLabel(selectedItem) : "", onChange: (e) => setSearch(e.target.value), onFocus: handleFocus, readOnly: !show && selectedItem !== null, crossOrigin: "", "aria-haspopup": "listbox", "aria-expanded": show, inputRef: inputRef, error: error, onKeyDown: handleKeyDown }), show && (_jsxs("div", { className: "absolute top-full left-0 w-full bg-white border border-gray-300 rounded z-10", role: "listbox", "aria-label": `Options de ${label}`, children: [filteredOptions.length > 0 ? (_jsx("ul", { className: "max-h-48 overflow-y-auto", children: filteredOptions.map((option) => (_jsx("li", { className: "px-3 py-2 hover:bg-gray-100 cursor-pointer", role: "option", onClick: () => handleSelect(option), children: optionLabel(option) }, optionLabel(option)))) })) : (_jsx("div", { className: "px-3 py-2 text-gray-500", children: "Aucune option disponible" })), allowCreate && trimmedSearch && !hasExactMatch && (_jsx("div", { className: "border-t border-gray-200", children: _jsx("button", { type: "button", className: "w-full text-left px-3 py-2 text-blue-600 hover:bg-blue-50 disabled:text-gray-400 disabled:cursor-not-allowed", onClick: () => void handleCreate(), disabled: isCreating, children: isCreating
                                ? "Création..."
                                : getCreateOptionLabel
                                    ? getCreateOptionLabel(trimmedSearch)
                                    : `Ajouter "${trimmedSearch}"` }) }))] }))] }));
}
export default SelectWithSearch;
